import { useState, useEffect } from 'react'
import { useAppStore } from '../../stores/app'
import { usePipelineConfigs, useUpdatePipelineConfigs } from '../../hooks/useAdminConfig'
import type { PipelineConfig } from '../../lib/api'
import { LoadingSpinner } from '../ui/LoadingSpinner'

type PipelineKey = 'orchestrator' | 'content' | 'visual' | 'strategy'

const PIPELINE_KEYS: PipelineKey[] = ['orchestrator', 'content', 'visual', 'strategy']

const RESEARCH_PROVIDERS = [
  { value: 'auto', label: 'Auto' },
  { value: 'tavily', label: 'Tavily' },
  { value: 'perplexity', label: 'Perplexity' },
  { value: 'google', label: 'Google' },
] as const

const SEARCH_RECENCY_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
] as const

function getConfigValue(config: Record<string, unknown>, key: string, fallback: number | boolean | string): number | boolean | string {
  const v = config[key]
  if (v === undefined || v === null) return fallback
  if (typeof fallback === 'number') return Number(v)
  if (typeof fallback === 'boolean') return Boolean(v)
  return String(v)
}

function configByKey(pipelines: PipelineConfig[]): Record<PipelineKey, Record<string, unknown>> {
  const out = {} as Record<PipelineKey, Record<string, unknown>>
  for (const key of PIPELINE_KEYS) {
    const p = pipelines.find((x) => x.pipeline_key === key)
    out[key] = p?.config ?? {}
  }
  return out
}

export function SettingsPipelinesSection() {
  const organizationId = useAppStore((state) => state.organizationId)
  const { data: pipelinesRaw = [], isLoading } = usePipelineConfigs(organizationId ?? undefined)
  const pipelines: PipelineConfig[] = Array.isArray(pipelinesRaw) ? pipelinesRaw : []
  const updatePipelineConfigs = useUpdatePipelineConfigs()

  const configs = configByKey(pipelines)

  const [orchestrator, setOrchestrator] = useState({
    max_iterations_per_task: 3,
    quality_threshold: 7,
    enable_visual_generation: true,
    enable_strategy_research: true,
  })
  const [content, setContent] = useState({
    max_iterations: 3,
    quality_threshold: 7,
  })
  const [visual, setVisual] = useState({
    max_refinements: 2,
    skip_brand_validation: false,
    enable_compositor: true,
  })
  const [strategy, setStrategy] = useState({
    enable_research: true,
    research_provider: 'auto',
    max_research_queries: 3,
    search_recency: 'month',
  })

  useEffect(() => {
    const o = configs.orchestrator
    setOrchestrator({
      max_iterations_per_task: getConfigValue(o, 'max_iterations_per_task', 3) as number,
      quality_threshold: getConfigValue(o, 'quality_threshold', 7) as number,
      enable_visual_generation: getConfigValue(o, 'enable_visual_generation', true) as boolean,
      enable_strategy_research: getConfigValue(o, 'enable_strategy_research', true) as boolean,
    })
    const c = configs.content
    setContent({
      max_iterations: getConfigValue(c, 'max_iterations', 3) as number,
      quality_threshold: getConfigValue(c, 'quality_threshold', 7) as number,
    })
    const v = configs.visual
    setVisual({
      max_refinements: getConfigValue(v, 'max_refinements', 2) as number,
      skip_brand_validation: getConfigValue(v, 'skip_brand_validation', false) as boolean,
      enable_compositor: getConfigValue(v, 'enable_compositor', true) as boolean,
    })
    const s = configs.strategy
    setStrategy({
      enable_research: getConfigValue(s, 'enable_research', true) as boolean,
      research_provider: getConfigValue(s, 'research_provider', 'auto') as string,
      max_research_queries: getConfigValue(s, 'max_research_queries', 3) as number,
      search_recency: getConfigValue(s, 'search_recency', 'month') as string,
    })
  }, [configs.orchestrator, configs.content, configs.visual, configs.strategy])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!organizationId) return
    await updatePipelineConfigs.mutateAsync({
      orgId: organizationId,
      data: {
        orchestrator: { ...orchestrator },
        content: { ...content },
        visual: { ...visual },
        strategy: { ...strategy },
      },
    })
  }

  const busy = updatePipelineConfigs.isPending

  if (isLoading) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-800">Pipelines</h2>
        <p className="text-sm text-slate-500 mt-1">Configuracion de pipelines (orchestrator, content, visual, strategy).</p>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </section>
    )
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-slate-800">Pipelines</h2>
      <p className="text-sm text-slate-500 mt-1">Configuracion de pipelines (orchestrator, content, visual, strategy).</p>

      <form onSubmit={handleSave} className="mt-6 space-y-8">
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">Orchestrator</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Max iterations per task</label>
              <input
                type="number"
                className="input"
                min={1}
                max={20}
                value={orchestrator.max_iterations_per_task}
                onChange={(e) => setOrchestrator((p) => ({ ...p, max_iterations_per_task: Number(e.target.value) }))}
                disabled={busy}
              />
            </div>
            <div>
              <label className="label">Quality threshold: {orchestrator.quality_threshold}</label>
              <input
                type="range"
                min={0}
                max={10}
                step={0.5}
                value={orchestrator.quality_threshold}
                onChange={(e) => setOrchestrator((p) => ({ ...p, quality_threshold: parseFloat(e.target.value) }))}
                disabled={busy}
                className="mt-1 w-full h-2 rounded-full appearance-none bg-slate-200 accent-slate-600"
              />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={orchestrator.enable_visual_generation}
                  onChange={(e) => setOrchestrator((p) => ({ ...p, enable_visual_generation: e.target.checked }))}
                  disabled={busy}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700">Enable visual generation</span>
              </label>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={orchestrator.enable_strategy_research}
                  onChange={(e) => setOrchestrator((p) => ({ ...p, enable_strategy_research: e.target.checked }))}
                  disabled={busy}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700">Enable strategy research</span>
              </label>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">Content</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Max iterations</label>
              <input
                type="number"
                className="input"
                min={1}
                max={20}
                value={content.max_iterations}
                onChange={(e) => setContent((p) => ({ ...p, max_iterations: Number(e.target.value) }))}
                disabled={busy}
              />
            </div>
            <div>
              <label className="label">Quality threshold: {content.quality_threshold}</label>
              <input
                type="range"
                min={0}
                max={10}
                step={0.5}
                value={content.quality_threshold}
                onChange={(e) => setContent((p) => ({ ...p, quality_threshold: parseFloat(e.target.value) }))}
                disabled={busy}
                className="mt-1 w-full h-2 rounded-full appearance-none bg-slate-200 accent-slate-600"
              />
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">Visual</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Max refinements</label>
              <input
                type="number"
                className="input"
                min={0}
                max={10}
                value={visual.max_refinements}
                onChange={(e) => setVisual((p) => ({ ...p, max_refinements: Number(e.target.value) }))}
                disabled={busy}
              />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visual.skip_brand_validation}
                  onChange={(e) => setVisual((p) => ({ ...p, skip_brand_validation: e.target.checked }))}
                  disabled={busy}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700">Skip brand validation</span>
              </label>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visual.enable_compositor}
                  onChange={(e) => setVisual((p) => ({ ...p, enable_compositor: e.target.checked }))}
                  disabled={busy}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700">Enable compositor</span>
              </label>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">Strategy</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-2 sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={strategy.enable_research}
                  onChange={(e) => setStrategy((p) => ({ ...p, enable_research: e.target.checked }))}
                  disabled={busy}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700">Enable research</span>
              </label>
            </div>
            <div>
              <label className="label">Research provider</label>
              <select
                className="input"
                value={strategy.research_provider}
                onChange={(e) => setStrategy((p) => ({ ...p, research_provider: e.target.value }))}
                disabled={busy}
              >
                {RESEARCH_PROVIDERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Max research queries</label>
              <input
                type="number"
                className="input"
                min={1}
                max={20}
                value={strategy.max_research_queries}
                onChange={(e) => setStrategy((p) => ({ ...p, max_research_queries: Number(e.target.value) }))}
                disabled={busy}
              />
            </div>
            <div>
              <label className="label">Search recency</label>
              <select
                className="input"
                value={strategy.search_recency}
                onChange={(e) => setStrategy((p) => ({ ...p, search_recency: e.target.value }))}
                disabled={busy}
              >
                {SEARCH_RECENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200">
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </section>
  )
}
